/**
 * RunTranscript — the answer-by-answer review of a finished run.
 *
 * For every scenario in play order it shows: the situation posed, the exact option the player chose
 * (with its quality tier via TierBadge — icon + label + colour, never colour alone), the effect
 * on each of the four meters, and the running totals after that step. The prioritisation pop-up shows
 * the player's ranking against each task's accepted band. Reconstructed by replayRun (game/replay.ts).
 *
 * Reused by the admin oversight panel (any player's run) and the player's own history.
 */
import { Fragment, useMemo } from "react";
import { ArrowDown, ArrowUp, Check, ChevronRight, Minus } from "lucide-react";
import type { DimensionId } from "@patc/content-schema";
import { gameContent } from "../content";
import type { Scores } from "../game/scoring";
import type { TranscriptEntry } from "../game/machine";
import { replayRun, type PopupStep, type ReplayStep } from "../game/replay";
import { PHASE_LABEL, TIER_STYLE, cx } from "../lib/ui";
import { Meters } from "./Meters";
import { TierBadge } from "./TierBadge";

const DIMS = gameContent.meta.dimensions;
const SHORT: Record<string, string> = {
  dealSecurity: "Deal security",
  clientSatisfaction: "Client",
  counterpartyCooperation: "Counterparty",
  transactionProcess: "Process",
};

/** One meter's movement on a step: signed delta + the running total it produced. */
function DeltaChip({ label, delta, after }: { label: string; delta: number; after: number }) {
  const Icon = delta > 0 ? ArrowUp : delta < 0 ? ArrowDown : Minus;
  const tone = delta > 0 ? "text-[var(--success)]" : delta < 0 ? "text-[var(--danger)]" : "text-ink-500";
  return (
    <div className="flex items-center justify-between gap-2 rounded-sm bg-ink-100 px-2 py-1">
      <span className="truncate text-xs text-ink-600">{label}</span>
      <span className="flex shrink-0 items-center gap-1 font-mono text-xs tabular-nums">
        <span className={cx("inline-flex items-center gap-0.5", tone)}>
          <Icon size={14} strokeWidth={2} aria-hidden="true" />
          {delta > 0 ? "+" : ""}
          {delta}
        </span>
        <span className="text-ink-500" aria-hidden="true">→</span>
        <span className="font-semibold text-ink">{after}</span>
      </span>
    </div>
  );
}

function PopupDetail({ step }: { step: PopupStep }) {
  return (
    <div className="mt-3 overflow-x-auto rounded-sm border border-ink-150">
      <table className="w-full text-sm">
        <thead className="bg-ink-100 text-left text-xs text-ink-500">
          <tr>
            <th className="px-2 py-1 font-medium">Task</th>
            <th className="px-2 py-1 font-medium">Ranked</th>
            <th className="px-2 py-1 font-medium">Accepted</th>
            <th className="px-2 py-1 font-medium">Result</th>
          </tr>
        </thead>
        <tbody>
          {step.rows.map((r) => (
            <tr key={r.taskId} className="border-t border-ink-150 align-top">
              <td className="px-2 py-1 text-ink">{r.text}</td>
              <td className="px-2 py-1 font-mono tabular-nums text-ink-700">{r.placed || "—"}</td>
              <td className="px-2 py-1 font-mono tabular-nums text-ink-500">
                {r.band[0] === r.band[1] ? r.band[0] : `${r.band[0]}–${r.band[1]}`}
              </td>
              <td className="px-2 py-1">
                {r.offBy === 0 ? (
                  <span className="inline-flex items-center gap-1 text-[var(--success)]">
                    <Check size={14} strokeWidth={2} aria-hidden="true" /> in band
                  </span>
                ) : (
                  <span className="text-[var(--danger)]">off by {r.offBy}</span>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function StepCard({ step }: { step: ReplayStep }) {
  return (
    <div className="rounded-sm border border-ink-150 bg-card p-4">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="min-w-0">
          <span className="t-overline text-ink-500">
            {step.n}. {PHASE_LABEL[step.phase]}
          </span>
          <h4 className="font-medium text-ink">{step.title}</h4>
        </div>
        {step.kind === "popup" ? (
          <span className="rounded-sm bg-ink-100 px-2 py-0.5 text-xs font-medium text-ink-600">
            Prioritisation {step.offByTotal === 0 ? "· all in band" : `· off by ${step.offByTotal}`}
          </span>
        ) : (
          step.choice && <TierBadge tier={step.choice.tier} />
        )}
      </div>

      <p className="mt-1.5 text-sm leading-relaxed text-ink-600">{step.prompt}</p>

      {step.kind === "popup" ? (
        <PopupDetail step={step} />
      ) : (
        <div
          className={cx(
            "mt-3 rounded-sm border-l-2 pl-3",
            step.choice ? TIER_STYLE[step.choice.tier].border : "border-ink-300",
          )}
        >
          <p className="t-overline text-ink-500">Answer</p>
          {step.choice ? (
            <>
              <p className="mt-0.5 text-sm leading-relaxed text-ink">
                <span className="font-mono text-ink-500">{step.choice.label}.</span> {step.choice.text}
              </p>
              {step.choice.reaction && (
                <p className="mt-1.5 text-sm leading-relaxed text-ink-600">
                  <span className="italic">{step.choice.reaction}</span>
                </p>
              )}
            </>
          ) : (
            <p className="mt-0.5 text-sm text-ink-500">No answer recorded.</p>
          )}
        </div>
      )}

      <div className="mt-3 grid grid-cols-2 gap-1.5 sm:grid-cols-4">
        {DIMS.map((d) => (
          <DeltaChip
            key={d.id}
            label={SHORT[d.id] ?? d.label}
            delta={step.delta[d.id as DimensionId]}
            after={step.scoresAfter[d.id as DimensionId]}
          />
        ))}
      </div>

      {step.kind !== "popup" && step.model && (
        <details className="mt-3">
          <summary className="inline-flex cursor-pointer items-center gap-1 text-sm font-medium text-ink marker:content-none hover:text-brand">
            <ChevronRight size={15} strokeWidth={2.5} aria-hidden="true" />
            Model answer &amp; principle
          </summary>
          <div className="mt-2 rounded-sm border-l-2 border-[var(--color-tier-best)] bg-panel px-3 py-2">
            <p className="text-sm leading-relaxed text-ink-700">
              <span className="font-semibold">
                {step.choice?.tier === "best" ? "You chose the model answer. " : "Model answer. "}
              </span>
              {step.model.text}
            </p>
            {step.model.principle && (
              <p className="mt-1.5 text-sm leading-relaxed text-ink-700">
                <span className="font-semibold">Principle. </span>
                {step.model.principle}
              </p>
            )}
          </div>
        </details>
      )}

      {step.kind === "popup" && step.debrief && (
        <details className="mt-3">
          <summary className="inline-flex cursor-pointer items-center gap-1 text-sm font-medium text-ink marker:content-none hover:text-brand">
            <ChevronRight size={15} strokeWidth={2.5} aria-hidden="true" />
            The priority that holds up
          </summary>
          <p className="mt-2 rounded-sm border-l-2 border-[var(--color-tier-best)] bg-panel px-3 py-2 text-sm leading-relaxed text-ink-700">
            {step.debrief}
          </p>
        </details>
      )}
    </div>
  );
}

/**
 * @param transcript the run's recorded steps (in play order)
 * @param scores the run's stored final scores (authoritative; falls back to the replayed totals)
 */
export function RunTranscript({
  transcript,
  scores,
  showProfile = true,
}: {
  transcript: TranscriptEntry[];
  scores?: Scores | null;
  /** Show the final-profile meters at the top (the end screen shows them already, so it passes false). */
  showProfile?: boolean;
}) {
  const { steps, finalScores } = useMemo(() => replayRun(transcript), [transcript]);

  if (steps.length === 0) {
    return <p className="text-sm text-ink-500">No recorded answers for this run.</p>;
  }

  return (
    <div>
      {showProfile && (
        <section className="rounded-sm border border-ink-150 bg-card p-4">
          <h4 className="t-overline mb-3 text-ink-500">Final profile</h4>
          <Meters dimensions={DIMS} scores={scores ?? finalScores} />
        </section>
      )}

      <div className={cx("flex flex-col gap-3", showProfile && "mt-4")}>
        {steps.map((s, i) => (
          <Fragment key={`${s.scenarioId}-${i}`}>
            {(i === 0 || steps[i - 1]?.phase !== s.phase) && (
              <h3 className="t-overline mt-2 text-brand">{PHASE_LABEL[s.phase]}</h3>
            )}
            <StepCard step={s} />
          </Fragment>
        ))}
      </div>
    </div>
  );
}
